import { useState } from "react";
import { Clock, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface CourseCreationFormProps {
  onCourseCreated: () => void;
  children: React.ReactNode;
}

const CourseCreationForm = ({ onCourseCreated, children }: CourseCreationFormProps) => {
  const [showSubmissionMessage, setShowSubmissionMessage] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const form = useForm({
    defaultValues: {
      courseType: "",
      subscriptionType: "weekly",
      description: "",
      duration: "",
      weeklyPrice: "",
      callCount: "",
    },
  });

  const onSubmit = async (data: any) => {
    console.log("Course submission:", data);
    
    try {
      let courseTitle = "";
      let dbCourseType = "";
      
      if (data.courseType === "subscription") {
        courseTitle = data.subscriptionType === "weekly" ? "Weekly Mentoring" : "Monthly Mentoring";
        dbCourseType = "subscription";
      } else {
        courseTitle = "Voice/Video Call";
        dbCourseType = "voiceCall";
      }
      
      const coursePayload: any = {
        title: courseTitle,
        description: data.description,
        course_type: dbCourseType,
        duration: data.duration ? parseInt(data.duration) : null,
        price: parseFloat(data.weeklyPrice),
        mentor_id: user?.id,
        status: 'pending',
        subscription_type: data.courseType === "subscription" ? data.subscriptionType : null,
        weekly_call_allowance: data.callCount ? parseInt(data.callCount) : null,
        currency: 'aud' // Ensure AUD currency for all new courses
      };

      console.log("Final course payload:", coursePayload);

      const { data: newCourse, error } = await supabase
        .from('courses')
        .insert([coursePayload])
        .select()
        .single();

      if (error) throw error;

      console.log("New course submitted:", newCourse);
      
      toast({
        title: "Course Submitted",
        description: "Your course has been submitted for approval.",
      });

      setShowSubmissionMessage(true);
      form.reset();
      onCourseCreated();
      
    } catch (error) {
      console.error("Error submitting course:", error);
      toast({
        title: "Error",
        description: "Failed to submit course. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getCourseTitle = () => {
    const courseType = form.watch("courseType");
    const subscriptionType = form.watch("subscriptionType");
    
    if (courseType === "subscription") {
      return subscriptionType === "weekly" ? "Weekly Mentoring" : "Monthly Mentoring";
    } else if (courseType === "call") {
      return "Voice/Video Call";
    }
    return "";
  };

  const getPriceLabel = () => {
    const courseType = form.watch("courseType");
    const subscriptionType = form.watch("subscriptionType");
    
    if (courseType === "subscription") {
      return subscriptionType === "weekly" ? "(per week)" : "(per month)";
    }
    return "";
  };

  const getCallCountLabel = () => {
    const subscriptionType = form.watch("subscriptionType");
    return subscriptionType === "weekly" ? "per Week" : "per Month";
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        {showSubmissionMessage ? (
          <div className="py-6 text-center">
            <div className="mb-4 bg-yellow-50 text-yellow-800 p-4 rounded-md">
              <p className="font-medium">The course is now with the admin for approval</p>
            </div>
            <Button 
              onClick={() => setShowSubmissionMessage(false)} 
              variant="outline"
            >
              Create Another Course
            </Button>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <FormField
                control={form.control}
                name="courseType"
                rules={{ required: "Please select a course type" }}
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel>Course Type</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="flex flex-col space-y-1"
                      >
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="subscription" />
                          </FormControl>
                          <FormLabel className="font-normal cursor-pointer">
                            Subscription based mentoring
                          </FormLabel>
                        </FormItem>
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="call" />
                          </FormControl>
                          <FormLabel className="font-normal cursor-pointer">
                            Voice/Video Call
                          </FormLabel>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch("courseType") === "subscription" && (
                <FormField
                  control={form.control}
                  name="subscriptionType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Subscription Type</FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select subscription type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly Mentoring</SelectItem>
                            <SelectItem value="monthly" disabled className="text-gray-400 cursor-not-allowed">
                              Monthly Mentoring (Coming Soon)
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {form.watch("courseType") && (
                <FormItem>
                  <FormLabel>Course Title</FormLabel>
                  <FormControl>
                    <Input 
                      value={getCourseTitle()}
                      disabled
                      className="bg-gray-50 text-gray-700"
                    />
                  </FormControl>
                </FormItem>
              )}
              
              <FormField
                control={form.control}
                name="description"
                rules={{ required: "Course description is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input placeholder="Describe what mentees will learn" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {form.watch("courseType") === "subscription" && (
                <FormField
                  control={form.control}
                  name="duration"
                  rules={{ 
                    validate: (value) => {
                      if (value && (isNaN(Number(value)) || Number(value) <= 0)) {
                        return "Duration must be a positive number";
                      }
                      return true;
                    }
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration (minutes)</FormLabel>
                      <FormControl>
                        <div className="flex items-center">
                          <Input 
                            type="number" 
                            placeholder="e.g. 60" 
                            {...field} 
                          />
                          <Clock className="ml-2 text-gray-400" size={18} />
                        </div>
                      </FormControl>
                      <FormDescription>
                        Set the call duration in minutes
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              
              {form.watch("courseType") === "subscription" && (
                <FormField
                  control={form.control}
                  name="callCount"
                  rules={{ 
                    validate: (value) => {
                      if (value && (isNaN(Number(value)) || Number(value) <= 0)) {
                        return "Call count must be a positive number";
                      }
                      return true;
                    }
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Number of Voice/Video Calls {getCallCountLabel()}</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="e.g. 2" 
                          {...field} 
                        />
                      </FormControl>
                      <FormDescription>
                        Optional: Set how many voice/video calls you'll provide {getCallCountLabel().toLowerCase()}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              
              {form.watch("courseType") !== "subscription" && (
                <FormField
                  control={form.control}
                  name="duration"
                  rules={{ required: form.watch("courseType") !== "subscription" ? "Duration is required" : false }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration (minutes)</FormLabel>
                      <FormControl>
                        <div className="flex items-center">
                          <Input 
                            type="number" 
                            placeholder="e.g. 60" 
                            {...field} 
                          />
                          <Clock className="ml-2 text-gray-400" size={18} />
                        </div>
                      </FormControl>
                      <FormDescription>
                        Set the call duration in minutes
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              
              <FormField
                control={form.control}
                name="weeklyPrice"
                rules={{ 
                  required: "Price is required",
                  validate: (value) => {
                    if (isNaN(Number(value)) || Number(value) < 5) {
                      return "Minimum price is A$5.00";
                    }
                    return true;
                  }
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price in AUD {getPriceLabel()}</FormLabel>
                    <FormControl>
                      <div className="flex items-center">
                        <span className="mr-2 text-gray-600">A$</span>
                        <Input 
                          type="number" 
                          placeholder="Minimum A$5.00" 
                          {...field}
                        />
                        <DollarSign className="ml-2 text-gray-400" size={18} />
                      </div>
                    </FormControl>
                    <FormDescription>
                      All prices are in Australian Dollars (AUD)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <DialogFooter>
                <Button type="submit" variant="action">Submit Course</Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CourseCreationForm;
